import type CardClient from '@atlaskit/link-provider/client';

import { getSmartLinkProductGroupLabel } from './groupSmartLinksByProduct.ts';
import { jsonLdToEnhancedSmartLink } from './jsonLdToEnhancedSmartLink.ts';
import type { EnhancedSmartLink } from './smartLinkTypes.ts';

type SmartLinkResponse = Parameters<typeof jsonLdToEnhancedSmartLink>[2];

export async function fetchProductSmartLinkData(
	links: EnhancedSmartLink[],
	productLabel: string,
	client: Pick<CardClient, 'fetchData'>,
	reportError: (error: unknown) => void,
): Promise<Map<string, SmartLinkResponse>> {
	const urls = new Set<string>();
	for (const link of links) {
		if (getSmartLinkProductGroupLabel(link) === productLabel) {
			urls.add(link.url);
		}
	}
	const dataByUrl = new Map<string, SmartLinkResponse>();
	await Promise.all(
		Array.from(urls, async (url) => {
			try {
				dataByUrl.set(url, await client.fetchData(url, true));
			} catch (error: unknown) {
				reportError(error);
			}
		}),
	);
	return dataByUrl;
}

const mergeRefreshedSmartLink = (
	link: EnhancedSmartLink,
	response: SmartLinkResponse,
): EnhancedSmartLink => {
	const refreshedLink = jsonLdToEnhancedSmartLink(link.url, link.occurrenceIndex, response);
	return {
		...refreshedLink,
		iconUrl: refreshedLink.iconUrl ?? link.iconUrl,
		product: link.product ?? refreshedLink.product,
		subproduct: link.subproduct ?? refreshedLink.subproduct,
	};
};

export function applySmartLinkRefresh(
	links: EnhancedSmartLink[],
	item: EnhancedSmartLink,
	productLabel: string,
	dataByUrl: ReadonlyMap<string, SmartLinkResponse>,
): { links: EnhancedSmartLink[]; matchingItem: EnhancedSmartLink | undefined } {
	let matchingItem: EnhancedSmartLink | undefined;
	const refreshedLinks = links.map((link) => {
		if (getSmartLinkProductGroupLabel(link) !== productLabel) {
			return link;
		}
		const response = dataByUrl.get(link.url);
		if (!response) {
			return link;
		}
		const refreshedLink = mergeRefreshedSmartLink(link, response);
		if (link.url === item.url && link.occurrenceIndex === item.occurrenceIndex) {
			matchingItem = refreshedLink;
		}
		return refreshedLink;
	});
	return { links: refreshedLinks, matchingItem };
}
